import { useCallback, useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";
import classNames from "classnames";
import isEmpty from "lodash/isEmpty";
import { IContentModel } from "models/dto/ZoomiLxp/Models/ContentModels/IContentModel";
import PlayerPdf from "../player-pdf/player-pdf";
import PlayerControl, { ControlParams } from "../player-control/player-control";
import { useAppStore } from "store";
import { EventTypes } from "models/dto/ZoomiLxp/Utilities/Enumerations/EventTypes";
import { ISegmentPositions } from "../player-layout/player-layout";
import { getFormattedProgress } from "helpers/string.helper";
import { usePlayerNav } from "hooks/usePlayerNav";
import { usePlayerControl } from "hooks/usePlayerControl";
import { ISaveEventCourseItemHistoryModel } from "models/dto/ZoomiLxp/Models/EventModels/ISaveEventCourseItemHistoryModel";
import isNull from "lodash/isNull";
import { IPlayerPropsBase } from "../data/data";
import { useContentDownload } from "hooks/useContentFiles";

interface IPlayerPdfBoxProps extends IPlayerPropsBase {
	content: IContentModel;
	segment?: ISegmentPositions;
}

const PlayerPdfBox = ({ content, segment, className, onEventHandle }: IPlayerPdfBoxProps) => {
	const { contentStore, playerStore } = useAppStore();
	const downloadFile = useContentDownload(content);
	const { prev, next } = usePlayerNav();
	const wasEndEventSentRef = useRef(false);
	const playingRef = useRef(false);
	const currentPositionRef = useRef(0);
	const sessionIdRef = useRef<number>(contentStore.sessionId ?? 0);
	const lastScrollYRef = useRef(0);

	const handleNext = async (settings: ControlParams) => {
		contentStore.stopKeepAlive();

		const data: ISaveEventCourseItemHistoryModel = {
			sessionId: contentStore?.sessionId ?? 0,
			eventType: EventTypes.Complete,
			payload: JSON.stringify({
				currentPosition: settings.currentProgress,
			}),
		};
		onEventHandle(data).then(() => {
			next();
		});
	};

	const handlePrev = async (settings: ControlParams) => {
		setParams((prevState: ControlParams) => ({ ...prevState, loading: true }));
		setParams((prevState: ControlParams) => ({ ...prevState, currentProgress: 1 }));

		contentStore.stopKeepAlive();
		const data: ISaveEventCourseItemHistoryModel = {
			sessionId: contentStore?.sessionId ?? 0,
			eventType: EventTypes.End,
			payload: JSON.stringify({
				currentPosition: settings.currentProgress,
			}),
		};
		onEventHandle(data).then(() => prev());
	};
	const handleSeekChange = (value: number) => {
		if (value > 0 && playerStore.heightPage > 0 && !isNull(playerStore.documentRef))
			playerStore.documentRef.current?.scrollTo({
				top: (value - 1) * playerStore.heightPage + 10,
				left: 0,
				behavior: "smooth",
			});
	};

	const initParams: ControlParams = {
		currentProgress: 1,
		totalProgress: 0,
		boxRef: useRef<HTMLDivElement>(null),
		getFormattedProgress,
		muted: true,
		playing: false,
		handleNext,
		handlePrev,
		isPrev: false,
		isNext: false,
		loading: true,
		isDownload: false,
		handleSeekChange,
	};

	const handleScrollSync = useCallback((y: number) => {
		lastScrollYRef.current = y;
	}, []);

	const sendEndEventOnce = async () => {
		if (wasEndEventSentRef.current) return;
		wasEndEventSentRef.current = true;

		const currentPosition = currentPositionRef.current;
		const isPlaying = playingRef.current;
		const sessionId = sessionIdRef.current;
		const posY = lastScrollYRef.current;

		const data: ISaveEventCourseItemHistoryModel = {
			sessionId,
			eventType: EventTypes.End,
			payload: JSON.stringify({
				currentPosition,
				isPlaying,
				posY,
			}),
		};

		contentStore.stopKeepAlive();

		await onEventHandle(data);
	};

	const [params, setParams] = useState<ControlParams>(initParams);

	useEffect(() => {
		playingRef.current = params.playing;
	}, [params.playing]);

	useEffect(() => {
		currentPositionRef.current = params.currentProgress ?? 0;
	}, [params.currentProgress]);

	useEffect(() => {
		if (contentStore.sessionId) {
			sessionIdRef.current = contentStore.sessionId;
		}
	}, [contentStore.sessionId]);

	usePlayerControl(setParams);

	useEffect(() => {
		const onBeforeUnload = () => {
			contentStore.stopKeepAlive();

			const data: ISaveEventCourseItemHistoryModel = {
				sessionId: contentStore?.sessionId ?? 0,
				eventType: EventTypes.End,
				payload: "",
			};
			onEventHandle(data);
		};
		window.addEventListener("beforeunload", onBeforeUnload);
		return () => window.removeEventListener("beforeunload", onBeforeUnload);
	}, [contentStore, onEventHandle]);

	useEffect(() => {
		playerStore.params = params;
	}, [params, playerStore]);

	useEffect(() => {
		setParams((prevState: ControlParams) => ({
			...prevState,
			isDownload: content.downloadable,
			handleDownload: content.downloadable ? downloadFile : undefined,
		}));
	}, [content.downloadable, downloadFile]);

	useEffect(
		() => {
			return () => {
				void sendEndEventOnce();
			};
		}, //eslint-disable-next-line
		[]
	);

	if (isEmpty(content.fileContent?.url)) return <div className={styles.player_pdf_box__nodata}>No data</div>;

	return (
		<div className={classNames(styles.player_pdf_box, className)}>
			<PlayerPdf
				content={content}
				params={params}
				setParams={setParams}
				key={content.id}
				segment={segment}
				onEventHandle={onEventHandle}
				onScrollSync={handleScrollSync}
			>
				<PlayerControl params={params} zoomLimitMultiplier={2} />
			</PlayerPdf>
		</div>
	);
};

export default PlayerPdfBox;
